import type { CreateTemplateOptions } from '@domains/subscription/priv/Templates/TemplateForm'
import { BillableItemsApiFactory, PricesApiFactory, type TemplatePriceRequest } from '@mynt/backoffice-api-spec/billables'
import { PartnersApiFactory } from '@mynt/backoffice-api-spec/partner'
import { BulkTemplateMigrationApiFactory, TemplatesApiFactory } from '@mynt/backoffice-api-spec/templates'
import Axios from 'api/axios'
import config from 'config'
import { countries } from 'countries-list'
import type { CreateDefaultConfigFormData, DefaultConfig, Eligibility, PricePlanTemplate } from '../types'
import type { CreateTemplateFromCopyPayload } from './api'
import {
  mapAssignedPriceToPrice,
  mapBillableItem,
  mapConnectedTemplateDtoToTemplate,
  mapCreateDefaultConfigFormDataToCreateDefaultConfigDto,
  mapDefaultConfigDtoToDefaultConfig,
  mapEligibilityToEligibilityDto,
  mapTemplateDtoToTemplate,
  mapToTemplateCreateDto,
} from './mappers'

const partnersApi = PartnersApiFactory(undefined, config.boBaseUrl, Axios)
const templatesApi = TemplatesApiFactory(undefined, config.boBaseUrl, Axios)
const billablesApi = BillableItemsApiFactory(undefined, config.boBaseUrl, Axios)
const pricesApi = PricesApiFactory(undefined, config.boBaseUrl, Axios)
const bulkMigrationApi = BulkTemplateMigrationApiFactory(undefined, config.boBaseUrl, Axios)

export const getTemplates = async (): Promise<PricePlanTemplate[]> => {
  return await templatesApi.getAllTemplates().then(({ data }) => data.map(mapConnectedTemplateDtoToTemplate))
}

export const getTemplate = async (id: string) => {
  return await templatesApi.getTemplate(id).then(({ data }) => mapConnectedTemplateDtoToTemplate(data))
}

export const updateTemplate = async (templateId: string, options: { name: string; externalName: string; eligibility: Eligibility }) => {
  const swaggerEligibility = mapEligibilityToEligibilityDto(options.eligibility)
  return await templatesApi
    .updateTemplate(templateId, { name: options.name, externalName: options.externalName, eligibility: swaggerEligibility })
    .then(({ data }) => mapTemplateDtoToTemplate(data))
}

export const createTemplate = async (name: string, externalName: string, eligibility: Eligibility) => {
  const swaggerEligibility = mapEligibilityToEligibilityDto(eligibility)
  return await templatesApi
    .createTemplate({ name, externalName, eligibility: swaggerEligibility })
    .then(({ data }) => mapTemplateDtoToTemplate(data))
}

export const createTemplateFromExisting = async ({ name, externalName, eligibility, templateId }: CreateTemplateFromCopyPayload) => {
  return await templatesApi
    .createTemplateFromExisting(templateId, mapToTemplateCreateDto(name, externalName, eligibility))
    .then(({ data }) => mapTemplateDtoToTemplate(data))
}

export const deleteTemplate = async (templateId: string): Promise<void> => {
  await templatesApi.deleteTemplate(templateId)
}

export const getBillables = () => billablesApi.getAllItems().then(({ data }) => data.map(mapBillableItem))

export const getTemplateForCustomer = (customerId: string) =>
  templatesApi.getTemplateForCustomer(customerId, { disableCustomErrorHandler: true }).then(({ data }) => mapTemplateDtoToTemplate(data))

interface DeleteBillableParams {
  billableId: string
  templateId: string
}

export const deleteBillable = ({ billableId, templateId }: DeleteBillableParams) =>
  pricesApi.removeBillableItemPriceOnATemplate(billableId, templateId)

export const getPricesForTemplate = (templateId: string) =>
  pricesApi.getAllBillableItemPricesOnATemplate(templateId).then(({ data }) => data.map(mapAssignedPriceToPrice))

interface SaveFeatureForTemplateRequest {
  templateId: string
  billableId: string
  priceRequest: TemplatePriceRequest
}

export const saveBillablePrice = ({ templateId, billableId, priceRequest }: SaveFeatureForTemplateRequest) =>
  pricesApi.createBillableItemPriceOnATemplate(billableId, templateId, priceRequest)

export const createTemplateOptions = (): Promise<CreateTemplateOptions> => {
  const partnersPromise = getPartners()
  const currenciesPromise = getCurrencies()
  const templatesPromise = getTemplates()

  const countryOptions = Object.entries(countries).map(([countryCode, countryData]) => ({
    label: countryData.name,
    value: countryCode,
  }))

  return Promise.all([partnersPromise, currenciesPromise, templatesPromise]).then(([partners, currencies, templates]) => ({
    partners: partners.map((partner) => partner.name),
    currencies,
    countries: countryOptions,
    templates: templates.map((template) => ({ name: template.name, value: template.id })),
  }))
}

export const getPartners = () => partnersApi.listPartners().then(({ data }) => data)
export const getCurrencies = () => templatesApi.getSupportedCurrencies().then(({ data }) => data)

export const getSupportedPartners = async (): Promise<string[]> =>
  partnersApi.listPartners().then(({ data }) => data.map((partner) => partner.name))

export const getSupportedCurrencies = async (): Promise<string[]> => await templatesApi.getSupportedCurrencies().then(({ data }) => data)

export const getDefaultConfigs = async (): Promise<DefaultConfig[]> =>
  await templatesApi.listDefaultTemplates().then(({ data }) => data.map(mapDefaultConfigDtoToDefaultConfig))

export const createDefaultConfig = async (formData: CreateDefaultConfigFormData): Promise<DefaultConfig> => {
  return templatesApi
    .assignDefaultTemplate(mapCreateDefaultConfigFormDataToCreateDefaultConfigDto(formData))
    .then(({ data }) => mapDefaultConfigDtoToDefaultConfig(data))
}

export const updateDefaultConfig = async (configId: string, formData: CreateDefaultConfigFormData): Promise<DefaultConfig> => {
  return templatesApi
    .updateDefaultTemplate(configId, mapCreateDefaultConfigFormDataToCreateDefaultConfigDto(formData))
    .then(({ data }) => mapDefaultConfigDtoToDefaultConfig(data))
}

export const removeDefaultConfig = async (configId: string): Promise<void> => {
  await templatesApi.removeDefaultTemplate(configId)
}

export const uploadMigrationFile = async (file: File) => {
  const { data } = await bulkMigrationApi.uploadMigrationFile(file)
  return data
}

export const startMigration = async (fileId: string) => {
  const { data } = await bulkMigrationApi.startMigration(fileId)
  return data
}

export const getMigrationStatus = async (fileId: string) => {
  const { data } = await bulkMigrationApi.getMigrationStatus(fileId)
  return data
}
